
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { format } from "date-fns";
import { 
  InventoryItem,
  getInventoryStatusColor,
  InventoryStatus,
} from "./inventoryTypes";
import { fetchInventoryItemHistory, useInventoryOperations } from "./inventoryService";
import { AssignItemDialog } from "./AssignItemDialog";
import { ReturnItemDialog } from "./ReturnItemDialog";
import { ConfirmationDialog } from "../ui/confirmation-dialog";

interface InventoryItemDialogProps {
  item: InventoryItem;
  open: boolean;
  onClose: (updated: boolean) => void;
}

export function InventoryItemDialog({ item, open, onClose }: InventoryItemDialogProps) {
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [showSoldDialog, setShowSoldDialog] = useState(false);
  const [showRetireDialog, setShowRetireDialog] = useState(false);
  const [updated, setUpdated] = useState(false);
  
  const { handleItemStatusUpdate } = useInventoryOperations();
  
  const { data: historyItems = [], isLoading: isHistoryLoading } = useQuery({
    queryKey: ["inventoryItemHistory", item.id],
    queryFn: () => fetchInventoryItemHistory(item.id),
  });

  const handleClose = () => {
    onClose(updated);
  };

  const handleAssignDialogClose = (success: boolean) => {
    setShowAssignDialog(false);
    if (success) setUpdated(true);
  };

  const handleReturnDialogClose = (success: boolean) => {
    setShowReturnDialog(false);
    if (success) setUpdated(true);
  };

  const handleSoldConfirm = async () => {
    const success = await handleItemStatusUpdate(item.id, "Sold");
    setShowSoldDialog(false);
    if (success) setUpdated(true);
  };

  const handleRetireConfirm = async () => {
    const success = await handleItemStatusUpdate(item.id, "Retired");
    setShowRetireDialog(false);
    if (success) setUpdated(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "MMM d, yyyy");
  };

  const canTransitionTo = (status: InventoryStatus) => {
    // Available ↔ Assigned ↔ Stored → Sold/Retired (terminal)
    if (item.status === 'Sold' || item.status === 'Retired') {
      return false; // Terminal states
    }
    
    if (status === 'Available') {
      return item.status === 'Assigned';
    }
    
    if (status === 'Assigned') {
      return item.status === 'Available';
    }
    
    // Sold and Retired can be reached from any non-terminal state
    return status === 'Sold' || status === 'Retired';
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              <span>Inventory Item - {item.serial}</span>
              <Badge 
                variant="outline"
                className={getInventoryStatusColor(item.status)}
              >
                {item.status}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              {item.description || "No description"}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Item Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Serial</p>
                    <p>{item.serial}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Category</p>
                    <p>{item.category}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Status</p>
                    <p>{item.status}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {item.status === 'Assigned' ? 'Assignee' : 'Location'}
                    </p>
                    <p>{item.assignee_name || item.location || "-"}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Purchase Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Cost</p>
                    <p>{item.cost ? `${item.currency} ${item.cost}` : "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Purchase Date</p>
                    <p>{formatDate(item.purchase_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Authorized By</p>
                    <p>{item.authorizer_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Payment Type</p>
                    <p>{item.payment_type}</p>
                  </div>
                  {item.payment_type === 'Recursive' && (
                    <>
                      <div>
                        <p className="text-sm font-medium">Payment Cadence</p>
                        <p>{item.payment_cadence}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Payment Amount</p>
                        <p>{item.payment_amount ? `${item.currency} ${item.payment_amount}` : "-"}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Next Payment Date</p>
                        <p>{formatDate(item.next_payment_date)}</p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>System Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium">Created At</p>
                    <p>{formatDate(item.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Last Updated</p>
                    <p>{formatDate(item.updated_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Status Last Changed</p>
                    <p>{formatDate(item.status_changed_at)}</p>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  {item.status !== 'Sold' && item.status !== 'Retired' && (
                    <div className="flex gap-2">
                      {canTransitionTo('Assigned') && (
                        <Button 
                          onClick={() => setShowAssignDialog(true)}
                          variant="outline"
                        >
                          Assign
                        </Button>
                      )}
                      {canTransitionTo('Available') && (
                        <Button 
                          onClick={() => setShowReturnDialog(true)}
                          variant="outline"
                        >
                          Return
                        </Button>
                      )}
                      {canTransitionTo('Sold') && (
                        <Button 
                          onClick={() => setShowSoldDialog(true)}
                          variant="outline"
                          className="text-rose-500 hover:text-rose-600"
                        >
                          Mark Sold
                        </Button>
                      )}
                      {canTransitionTo('Retired') && (
                        <Button 
                          onClick={() => setShowRetireDialog(true)}
                          variant="outline"
                          className="text-gray-500 hover:text-gray-600"
                        >
                          Retire
                        </Button>
                      )}
                    </div>
                  )}
                </CardFooter>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <Card>
                <CardHeader>
                  <CardTitle>Item History</CardTitle>
                  <CardDescription>Timeline of changes to this item</CardDescription>
                </CardHeader>
                <CardContent>
                  {isHistoryLoading ? (
                    <div className="text-center py-4">Loading history...</div>
                  ) : historyItems.length === 0 ? (
                    <div className="text-center py-4">No history records found</div>
                  ) : (
                    <div className="space-y-4">
                      {historyItems.map((historyItem) => (
                        <div key={historyItem.id} className="border-l-2 border-muted pl-4 pb-4 relative">
                          <div className="h-3 w-3 absolute -left-[7px] top-0 rounded-full bg-primary" />
                          <div className="flex justify-between">
                            <Badge variant="outline">
                              {historyItem.event_type}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(historyItem.event_at), "MMM d, yyyy h:mm a")}
                            </span>
                          </div>
                          <p className="text-sm mt-1">
                            {historyItem.actor_name ? (
                              <>Action by: {historyItem.actor_name}</>
                            ) : (
                              <>System generated</>
                            )}
                          </p>
                          {historyItem.details?.changes && (
                            <div className="text-xs mt-2 text-muted-foreground space-y-1">
                              {Object.entries(historyItem.details.changes).map(([key, value]: [string, any]) => (
                                <p key={key}>Changed {key}: {JSON.stringify(value)}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {showAssignDialog && (
        <AssignItemDialog 
          item={item}
          open={showAssignDialog}
          onClose={handleAssignDialogClose}
        />
      )}

      {showReturnDialog && (
        <ReturnItemDialog
          item={item}
          open={showReturnDialog}
          onClose={handleReturnDialogClose}
        />
      )}

      <ConfirmationDialog
        open={showSoldDialog}
        onClose={() => setShowSoldDialog(false)}
        onConfirm={handleSoldConfirm}
        title="Mark Item as Sold"
        description="Are you sure you want to mark this item as sold? This action cannot be undone."
        confirmText="Mark Sold"
        cancelText="Cancel"
      />

      <ConfirmationDialog
        open={showRetireDialog}
        onClose={() => setShowRetireDialog(false)}
        onConfirm={handleRetireConfirm}
        title="Retire Item"
        description="Are you sure you want to retire this item? This action cannot be undone."
        confirmText="Retire"
        cancelText="Cancel"
      />
    </>
  );
}
